/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classname from 'classnames';
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    Button,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    TelemetryConstant,
    getTelemetryObject,
    getPayloadObject,
    getTelemetryAttributes
} from '@msdyn365-commerce-modules/utilities';

import { IGuestCheckoutOnAccountThankYouData } from './guest-checkout-on-account-thank-you.data';
import { IGuestCheckoutOnAccountThankYouProps } from './guest-checkout-on-account-thank-you.props.autogenerated';

export interface IGuestCheckoutOnAccountThankYouViewProps
    extends IGuestCheckoutOnAccountThankYouProps<IGuestCheckoutOnAccountThankYouData> {
    moduleProps: IModuleProps;
    heading: React.ReactNode;
    orderId: React.ReactNode;
    thankYou: React.ReactNode;
    successMessage: React.ReactNode;
    backToShoppingLink?: React.ReactNode;
    footerProps?: INodeProps;
}

/**
 *
 * GuestCheckoutOnAccountThankYou component
 * @extends {React.PureComponent<IGuestCheckoutOnAccountThankYouProps<IGuestCheckoutOnAccountThankYouData>>}
 */
class GuestCheckoutOnAccountThankYou extends React.PureComponent<
    IGuestCheckoutOnAccountThankYouProps<IGuestCheckoutOnAccountThankYouData>
> {
    private readonly telemetryContent?: ITelemetryContent;
    private readonly backToShoppingAttributes?: Msdyn365.IDictionary<string>;

    constructor(props: IGuestCheckoutOnAccountThankYouProps<IGuestCheckoutOnAccountThankYouData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.BackToShopping);
        this.backToShoppingAttributes = getTelemetryAttributes(this.telemetryContent, payLoad);
    }
    public render(): JSX.Element | null {
        const {
            config: { heading, orderId, thankYou, successMessage },
            resources: { backToShopping },
            context: { request }
        } = this.props;

        const moduleProps = { moduleProps: this.props, className: classname('ms-order-confirmation', this.props.config.className) };
        const backToShoppingUrl = Msdyn365.getUrlSync('home', this.props.context.actionContext) || '';
        const viewProps: IGuestCheckoutOnAccountThankYouViewProps = {
            ...this.props,
            moduleProps,
            heading: heading && heading.text && (
                <Msdyn365.Text
                    {...heading}
                    className='ms-order-confirmation__heading'
                    text={heading.text}
                    tag={heading.tag || 'h2'}
                    editProps={{ onEdit: this.handleOrderConfirmationHeadingChange, requestContext: request }}
                />
            ),
            backToShoppingLink: backToShoppingUrl && (
                <Button
                    className='ms-order-confirmation__btn-keep-shopping msc-btn'
                    href={backToShoppingUrl}
                    {...this.backToShoppingAttributes}
                >
                    {backToShopping}
                </Button>
            ),
            orderId: request.query?.orderId ? (
                <span className='ms-order-confirmation__order-information-channel-reference-id'>
                    {orderId}: <span>{request.query.orderId}</span>
                </span>
            ) : null,
            thankYou: <h3>{thankYou}</h3>,
            successMessage: <span>{successMessage}</span>,
            footerProps: { className: 'ms-order-confirmation__footer' }
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }
    public handleOrderConfirmationHeadingChange = (event: Msdyn365.ContentEditableEvent) =>
        (this.props.config.heading!.text = event.target.value);
}

export default GuestCheckoutOnAccountThankYou;
